"use client";

import { useSearchParams } from "next/navigation";
import { Suspense, useOptimistic } from "react";
import type { ProductGetResult } from "@/dao/product";
import { useTranslations } from "@/i18n/client";
import { cn } from "@/lib/utils";
import { AddToCartButton } from "@/ui/storefront/AddToCartButton";
import { findSelectedVariant } from "@/ui/yns-components/components/product-details/find-selected-variant";
import type { ProductDetails } from "@/ui/yns-components/components/product-details/product-details";
import { ProductVariantSelector } from "@/ui/yns-components/components/product-details/product-variant-selector";
import type { YnsThemeType } from "@/ui/yns-components/components/types";

// Made this a client component to use `useOptimistic`
// to speed up enabling the "Add to cart" button after selecting a variant
export const ProductDetailsInner = ({
	product,
	theme,
	domain,
}: {
	product: ProductGetResult;
	theme: YnsThemeType<typeof ProductDetails>;
	domain: string;
}) => {
	const searchParams_ = Object.fromEntries(useSearchParams().entries());

	const [optimisticSearchParams, setOptimisticSearchParams] = useOptimistic(
		searchParams_,
		(state: typeof searchParams_, action: typeof searchParams_) => {
			return action ?? state;
		},
	);

	const selectedVariant = findSelectedVariant(product.variants, optimisticSearchParams);
	const t = useTranslations("/product.page");

	const combinationsCount = product.variants.flatMap((c) => c.combinations).length;

	return (
		<>
			{combinationsCount > 0 && (
				<div className="grid gap-2">
					<p className="sr-only" id="variant-label">
						{t("variantTitle")}
					</p>
					<Suspense>
						<ProductVariantSelector
							variants={product.variants}
							optimisticSearchParams={optimisticSearchParams}
							setOptimisticSearchParams={setOptimisticSearchParams}
						/>
					</Suspense>
				</div>
			)}

			<AddToCartButton
				style={{
					"--theme-product-details-add-to-cart-button-color": theme.buttonTextColor ?? undefined,
					"--theme-product-details-add-to-cart-button-background-color":
						theme.buttonBackgroundColor ?? undefined,
					"--theme-product-details-add-to-cart-button-hover-background-color":
						theme.buttonHoverBackgroundColor ?? undefined,
					"--theme-product-details-price-color": theme.priceColor ?? undefined,
				}}
				className={cn(
					"text-[var(--theme-product-details-add-to-cart-button-color,#fff)]",
					"bg-[var(--theme-product-details-add-to-cart-button-background-color,#000)]",
					"hover:bg-[var(--theme-product-details-add-to-cart-button-hover-background-color,#222)]",
				)}
				variant={selectedVariant}
				product={product}
				domain={domain}
				disabled={
					!selectedVariant ||
					(typeof selectedVariant?.stock === "number" ? selectedVariant.stock <= 0 : false)
				}
			/>
		</>
	);
};
